import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, UtensilsCrossed, Heart, Gift } from 'lucide-react';
import useStore, { BRAND_COLORS } from '@/store/useStore';
import useStoreContext from '@/hooks/useStoreContext';
import { storeRoute } from '@/lib/routes';
import { apiGetStoreInfo } from '@/services/api';
import PwaInstallPrompt from '@/components/guest/PwaInstallPrompt';

function hexToRgbString(hex) {
  const safe = (hex || '').replace('#', '');
  if (safe.length !== 6) return '14, 122, 98';
  const n = Number.parseInt(safe, 16);
  return `${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}`;
}

function resolveBrandPalette(color) {
  if (String(color || '').toLowerCase() === '#a5f2f3') return BRAND_COLORS.brand;
  if (String(color || '').toLowerCase() === '#5f9ea0') return BRAND_COLORS.brand;
  if (color && BRAND_COLORS[color]) return BRAND_COLORS[color];
  if (typeof color === 'string' && /^#[0-9a-fA-F]{6}$/.test(color)) {
    return { hex: color, start: color, end: color };
  }
  return BRAND_COLORS.brand;
}

function shadeHex(hex, factor) {
  const safe = (hex || '').replace('#', '');
  if (safe.length !== 6) return '#0E7A62';
  let r = parseInt(safe.slice(0, 2), 16);
  let g = parseInt(safe.slice(2, 4), 16);
  let b = parseInt(safe.slice(4, 6), 16);
  if (factor >= 0) {
    r = Math.round(r + (255 - r) * factor);
    g = Math.round(g + (255 - g) * factor);
    b = Math.round(b + (255 - b) * factor);
  } else {
    r = Math.round(r * (1 + factor));
    g = Math.round(g * (1 + factor));
    b = Math.round(b * (1 + factor));
  }
  return `#${Math.max(0, Math.min(255, r)).toString(16).padStart(2, '0')}${Math.max(0, Math.min(255, g)).toString(16).padStart(2, '0')}${Math.max(0, Math.min(255, b)).toString(16).padStart(2, '0')}`;
}

export default function LandingPage() {
  const navigate = useNavigate();
  const merchantId = useStoreContext();
  const { guestToken, guestUser, setCurrentMerchantId, setStoreInfo, storeInfo, guestTheme } = useStore();
  const activeStoreInfo = storeInfo?.id === merchantId ? storeInfo : null;

  useEffect(() => {
    setCurrentMerchantId(merchantId);
    // Clear stale persisted store context first to avoid wrong-name flash.
    setStoreInfo(null);
    let mounted = true;
    apiGetStoreInfo(merchantId)
      .then((info) => {
        if (mounted) setStoreInfo(info);
      })
      .catch(() => {});
    return () => {
      mounted = false;
    };
  }, [merchantId, setCurrentMerchantId, setStoreInfo]);

  useEffect(() => {
    const colorKey = activeStoreInfo?.brand_color;
    const palette = resolveBrandPalette(colorKey);
    const base = palette.hex;
    const start = palette.start || base;
    const end = palette.end || base;
    const appliedStart = guestTheme === 'dark' ? shadeHex(start, -0.28) : start;
    const appliedEnd = guestTheme === 'dark' ? shadeHex(end, -0.4) : end;
    const appliedSolid = guestTheme === 'dark' ? shadeHex(base, -0.34) : base;
    document.documentElement.style.setProperty('--brand-start', appliedStart);
    document.documentElement.style.setProperty('--brand-end', appliedEnd);
    document.documentElement.style.setProperty('--brand-solid', appliedSolid);
    document.documentElement.style.setProperty('--brand-rgb', hexToRgbString(appliedSolid));
  }, [activeStoreInfo?.brand_color, guestTheme]);

  const hasMatchingSession = !!guestToken && guestUser?.merchant_id === merchantId;
  const handleGetStarted = () => {
    if (hasMatchingSession) {
      navigate(storeRoute(merchantId, 'wallet'));
      return;
    }
    if (guestToken && guestUser?.merchant_id && guestUser.merchant_id !== merchantId) {
      window.localStorage.setItem('flyback_entry_merchant_id', merchantId);
      window.localStorage.setItem('entry_merchant_id', merchantId);
      navigate('/store', { replace: true });
      return;
    }
    navigate(storeRoute(merchantId, 'login'));
  };

  return (
    <div className={`${guestTheme === 'dark' ? 'dark ' : ''}relative min-h-screen flex flex-col`} data-testid="landing-page">
      {/* Background */}
      <div className="absolute inset-0 z-0">
        <img src="https://images.unsplash.com/photo-1671197346374-9d03a2a33a92?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwxfHxhcHBldGl6aW5nJTIwcmVzdGF1cmFudCUyMGJsdXJyZWQlMjBiYWNrZ3JvdW5kfGVufDB8fHx8MTc3NjIyOTYxOXww&ixlib=rb-4.1.0&q=85" alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/40" />
      </div>

      <div className="relative z-10 flex-1 flex flex-col items-center justify-center px-8 text-center max-w-md mx-auto w-full">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="space-y-5 w-full">
          {/* Logo */}
          <motion.div
  initial={{ opacity: 0, scale: 0.9 }}
  animate={{ opacity: 1, scale: 1 }}
  transition={{ delay: 0.1 }}
>
  <div className="w-16 h-16 rounded-2xl bg-stone-800/60 border border-stone-600/40 flex items-center justify-center mx-auto backdrop-blur-sm">
    
    <img
      src="/assets/flyback.png"
      alt="Flyback"
      className="w-12 h-12 object-contain"
    />

  </div>
</motion.div>

          {/* Title */}
          <motion.h1 initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
            className="text-4xl sm:text-5xl font-bold text-white tracking-tight leading-tight" style={{ fontFamily: "'Bricolage Grotesque', sans-serif" }}>
            FlyBack <span className="text-brand">Rewards</span>
          </motion.h1>

          {/* Subtitle */}
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.35 }} className="space-y-1">
            <p className="text-white/90 text-xs font-semibold tracking-wider uppercase">{activeStoreInfo?.restaurant_name || 'Restaurant'}</p>
            <p className="text-white/80 text-base">Earn points on every visit.</p>
            <p className="text-white/60 text-sm bg-white/10 inline-block px-4 py-1 rounded-full backdrop-blur-sm">100 points = $1.</p>
          </motion.div>

          {/* Buttons */}
          <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }} className="space-y-3 pt-4 w-full">
            <button data-testid="get-started-btn" onClick={handleGetStarted}
              className="w-full h-14 rounded-full gradient-brand-btn text-base font-semibold flex items-center justify-center gap-2">
              Get Started <ArrowRight className="w-5 h-5" />
            </button>
          </motion.div>

          {/* Icons Row */}
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.7 }} className="flex justify-center gap-12 pt-6">
            {[{ icon: UtensilsCrossed, label: 'DINE' }, { icon: Heart, label: 'EARN' }, { icon: Gift, label: 'REDEEM' }].map(({ icon: Icon, label }) => (
              <div key={label} className="flex flex-col items-center gap-1.5">
                <Icon className="w-5 h-5 text-white/70" />
                <span className="text-[10px] text-white/50 font-bold tracking-widest">{label}</span>
              </div>
            ))}
          </motion.div>

          {/* Powered By */}
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.8 }} className="text-white/30 text-xs pt-4">
            Powered by <span className="font-semibold text-white/40">FlyBack</span>
          </motion.p>
        </motion.div>
      </div>
      <PwaInstallPrompt />
    </div>
  );
}
